import { useEffect, useState } from "react";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import Toast from "../../../components/toast";
import ToastContainer from "../../../components/toastContainer";
import TechnicalRiderModal from "../TechnicalRiderModal";
import {
  deleteTechnicalRider,
  getTechnicalRiders,
} from "../../../api/techRiderApi";
import PageLoader from "../../../components/pageLoader";
import DeleteConfirmationModal from "../../../components/deleteModal";
import { updateArtist } from "../../../api/artistApi";
import ArtistTechHospitalityModal from "../ArtistTechHopitalityModal";

const TechnicalHospitalityTab = ({ artist, onRefreshArtist }) => {
  //const [showCreateModal, setShowCreateModal] = useState(false);

  const [activeItem, setActiveItem] = useState(null); // null | technicalItem
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [toast, setToast] = useState(null);

  const [activeSection, setActiveSection] = useState(null);
  const [saving, setSaving] = useState(false);

  const [techRiders, setTechRiders] = useState([]);
  const [loading, setLoading] = useState(true);

  //const [editingItem, setEditingItem] = useState(null);

  const [deleteRiderId, setDeleteRiderId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchRiders = async () => {
    try {
      setLoading(true);
      const data = await getTechnicalRiders(artist.id);
      const list = Array.isArray(data) ? data : data.results || [];
      setTechRiders(list);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || "Failed to load technical riders",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteTechnicalRider(deleteRiderId);

      setToast({
        type: "success",
        message: "Technical Rider deleted successfully",
      });
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      fetchRiders();
    }
  };

  const handleUpdate = async (payload, section, setError) => {
    try {
      setSaving(true);

      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        formData.append(key, value);
      });

      await updateArtist(artist.id, formData);

      setActiveSection(null);
      onRefreshArtist();
    } catch (err) {
      setError(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (artist?.id) {
      fetchRiders();
      setDeleteRiderId(null);
    }
  }, [artist?.id]);

  const InfoCard = ({ title, section, children }) => (
    <div className="bg-slate-900/80 border border-white/10 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-semibold">{title}</h3>
        <button
          onClick={() => setActiveSection(section)}
          className="text-xs text-pink-500 hover:underline"
        >
          Edit
        </button>
      </div>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );

  const Info = ({ label, value, pdf }) => (
    <div>
      <span className="text-slate-400">{label}: </span>
      {pdf ? (
        <button
          onClick={() => window.open(pdf, "_blank")}
          className="text-pink-500 hover:text-pink-400"
        >
          <DocumentTextIcon className="w-5 h-5 inline" />
        </button>
      ) : (
        <span className="text-white">{value || "-"}</span>
      )}
    </div>
  );

  const StarDisplay = ({ value = 0, max = 5 }) => (
    <div className="flex items-center gap-1">
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          className={i < value ? "text-yellow-400" : "text-slate-600"}
        >
          ★
        </span>
      ))}
    </div>
  );

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6 p-4">
      {/* Top 3 Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Technical Info */}
        <InfoCard title="Technical Info" section="technical">
          <Info label="DJ Booth" value={artist.dj_booth} />
          <Info label="No of Players" value={artist.no_of_players} />
          <Info label="Type of Players" value={artist.type_of_players} />
          <Info label="Mixer Type" value={artist.mixer_type} />
          <Info label="Monitor Tops Info" value={artist.monitor_tops_info} />
          <Info label="Monitor Subs Info" value={artist.monitor_subs_info} />
          <Info
            label="Other Technical Info"
            value={artist.other_technical_info}
          />
          <Info label="Full Document" pdf={artist.technical_document_pdf} />
        </InfoCard>

        {/* Hospitality Info */}
        <InfoCard title="Hospitality Info" section="hospitality">
          <Info
            label="Hotel Rating Required"
            value={<StarDisplay value={artist.hotel_rating_required} max={7} />}
          />

          <Info label="Hotel Hospitality" value={artist.hotel_hospitality} />
          <Info
            label="DJ Booth Hospitality"
            value={artist.dj_booth_hospitality}
          />
          <Info label="DJ Booth Access" value={artist.dj_booth_access} />
          <Info label="DJ Booth Security" value={artist.dj_booth_security} />
          <Info
            label="Guest List Requirements"
            value={artist.guest_list_requirements}
          />
          <Info label="Full Document" pdf={artist.hospitality_document_pdf} />
        </InfoCard>

        {/* Transfers Info */}
        <InfoCard title="Transfers Info" section="transfers">
          <Info
            label="Car / Van Required"
            value={artist.is_car_required ? "Yes" : "No"}
          />
          <Info label="No of People" value={artist.no_of_people} />
          <Info
            label="Accepted Car Models"
            value={artist.accepted_car_models}
          />
          <Info
            label="Special Car Requirements"
            value={artist.special_car_requirements}
          />
        </InfoCard>
      </div>

      {/* Technical Rider Section */}
      <div className="bg-slate-900/80 border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Technical Rider</h3>

          <button
            onClick={() => {
              // setEditingItem(null);
              // setShowCreateModal(true);
              setActiveItem(null);
              setIsModalOpen(true);
            }}
            className="flex items-center gap-1 text-sm text-pink-500 hover:text-pink-400"
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-slate-400">
              <tr>
                <th className="text-left py-2">Item Name</th>
                <th className="text-left py-2">Link</th>
                <th className="text-center py-2">PDF</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>

            <tbody>
              {(techRiders || []).map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-white/5 hover:bg-slate-800/40 transition"
                >
                  <td className="py-2 text-white">{item.name}</td>

                  <td className="py-2">
                    {item.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-pink-500 hover:underline"
                      >
                        View
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  <td className="py-2 text-center">
                    {item.pdf_file ? (
                      <button
                        onClick={() => window.open(item.pdf_file, "_blank")}
                        className="text-pink-500"
                      >
                        <DocumentTextIcon className="w-5 h-5 inline" />
                      </button>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  <td className="py-2 text-right space-x-3">
                    <button
                      onClick={() => {
                        setActiveItem(item);
                        setIsModalOpen(true);
                      }}
                      className="text-slate-400 hover:text-white"
                    >
                      <PencilIcon className="w-4 h-4 inline" />
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteModal(true);
                        setDeleteRiderId(item.id);
                      }}
                      className="text-red-400 hover:text-red-500"
                    >
                      <TrashIcon className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}

              {!techRiders.length && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-slate-400">
                    No technical rider items added
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ArtistTechHospitalityModal
        isOpen={!!activeSection}
        section={activeSection}
        artist={artist}
        onClose={() => setActiveSection(null)}
        onSubmit={handleUpdate}
        loading={saving}
      />

      <TechnicalRiderModal
        isOpen={isModalOpen} //showCreateModal || !!editingItem
        onClose={() => {
          setActiveItem(null);
          setIsModalOpen(false);
          // setShowCreateModal(false);
          // setEditingItem(null);
        }}
        initialData={activeItem}
        artistId={artist.id}
        onSuccess={() => {
          // fetchRiders();
          // setShowCreateModal(false);
          // setEditingItem(null);
          setActiveItem(null);
          setIsModalOpen(false);
          onRefreshArtist();
          // setToast({
          //   type: "success",
          //   message: "Technical rider added successfully",
          // });
        }}
      />

      {/* Delete Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Technical Rider"
        description="Are you sure you want to delete this rider? This action cannot be undone."
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default TechnicalHospitalityTab;
